// Shared project record drawer (round 4). One overlay host for the current
// project record, opened from Portfolio rows and the Updates "Record" button.
// Reuses ProjectFullPage recordOnly (no rebuilt sections) inside SlideSheet.
//
// Guard contract (owner-facing):
//   - Editor state comes from ProjectFullPage.onEditorStateChange
//     ({ editing, busy }) aggregated over the edit modal, schedule and every
//     record section - no DOM heuristics.
//   - requestClose(): closes when clean; when editing, shows a discard
//     confirm; when busy (save in flight), never closes - shows a wait status
//     and Discard is disabled. Escape/scrim are ignored while a stacked modal
//     (record edit dialog, z-index 10000 above the sheet's 1101) is open.
//   - guardRef.current() -> { dirty, busy }. Owners must not switch the
//     drawer's project or unmount it while dirty; never while busy.
//   - In-app navigation (anchor clicks, back/forward) is blocked through
//     recordNavigationGuard while dirty; a busy drawer refuses to proceed.
//   - The SlideSheet panel itself is the dialog (role/aria-modal/name), so
//     the Close button is inside the named dialog. Tab/Shift+Tab are
//     contained in the panel; focus returns to the opener on close.
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UNSAFE_LocationContext, UNSAFE_NavigationContext } from "react-router-dom";
import { T, LAYERS } from "../../app/tokens.js";
import { SlideSheet } from "../../components/SlideSheet.jsx";
import { RecordDrawerHeader } from "../../components/RecordDrawerHeader.jsx";
import { ProjectFullPage } from "./ProjectFullPage.jsx";
import { getProjectMedia, onProjectMediaChanged } from "../../data/projectMediaClient.js";
import { safeMediaUrl } from "./media/MediaStrip.jsx";
import { CoverBand } from "../../components/RecordCoverBand.jsx";
import { useOfficeCover } from "../locations/useOfficeCover.js";
import { RecordReturnLink } from "../../components/RecordReturnLink.jsx";
import { InternalLink } from "../../components/InternalLink.jsx";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { buildLocationUrl, buildProjectUpdatesUrl, buildProjectFullUrl } from "../../services/buildUrl.js";
import { participationChip } from "./participationSummary.js";
import { headerMeta, phaseCell, budgetCell, fdobCell, statusCell } from "./drawer/projectHeaderModel.js";
import { isTerminalProject } from "../../lib/projectStage.js";
import { ProjectStagePill } from "./ProjectStageControl.jsx";
import { guardRouterNavigator, registerNavigationGuard, syncNavigationIndex } from "./recordNavigationGuard.js";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const PANEL_SEL = "[data-testid='project-record-drawer']";
const IDLE = Object.freeze({ editing: false, busy: false });
const btn = { border: `1px solid ${T.border}`, background: T.card, borderRadius: 8, padding: "5px 11px", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, cursor: "pointer" };

/** Pure: provenance copy for the record shown in the drawer. */
export function recordProvenance({ frozen = false, readOnly = false, origin = "portfolio" } = {}) {
  if (frozen) return "Current project record - today's metadata, not part of this week's frozen archive. Read-only here.";
  if (readOnly && origin === "updates") return "Current project record - today's metadata, read-only outside the live week.";
  return "Current project record - PMO-owned, separate from weekly updates.";
}

/** Pure: what a close/switch/navigation request may do given editor state. */
export function drawerCloseDecision({ editing = false, busy = false } = {}) {
  if (busy) return "wait";
  if (editing) return "confirm";
  return "close";
}

/** True when another modal dialog (not the drawer panel) is open above it. */
export function stackedDialogOpen(panel) {
  return [...document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"]')]
    .some((d) => d !== panel && !(panel && panel.contains(d)) && !d.contains(panel));
}

/** Pure: the drawer's cover descriptor, or null for the plain header. */
export function coverFromMedia(result) {
  const c = result?.effectiveCover;
  if (!c || c.type !== "image") return null;
  const src = safeMediaUrl(c.url);
  return src ? { id: c.id, src, caption: typeof c.caption === "string" ? c.caption : "" } : null;
}

/** Effective cover for the shown project; never exposes another project's. */
export function useProjectCover(projectId, enabled) {
  const [state, setState] = useState({ projectId: null, cover: null });
  const seq = useRef(0);
  useEffect(() => {
    // Every (re)open starts plain until a fresh read for this project lands.
    seq.current += 1;
    setState({ projectId: null, cover: null });
    if (!enabled || !projectId) return undefined;
    let active = true;
    // Latest request wins: an earlier GET can never overwrite a later refresh.
    // Only successful reads replace the confirmed decoration, including empty.
    const load = () => {
      const mine = ++seq.current;
      Promise.resolve().then(() => getProjectMedia(projectId))
        .then((r) => { if (active && mine === seq.current) setState({ projectId, cover: r.intentionalEmpty ? { intentionalEmpty:true } : coverFromMedia(r) }); })
        .catch(() => { /* The gallery owns the retryable error; keep last-good decoration. */ });
    };
    load();
    const off = onProjectMediaChanged(projectId, load);
    return () => { active = false; off(); };
  }, [projectId, enabled]);
  return state.projectId === projectId ? state.cover : null;
}

export const PROJECT_HEADER_HEIGHT = 184;
export const PROJECT_STRIP_HEIGHT = 68;
const oneLine = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 };
const cellLabel = { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".08em", textTransform: "uppercase", color: T.mid };

function StripCell({ label, cell, testId, loading }) {
  return (
    <div data-testid={testId} data-gap={cell?.gap ? "true" : undefined} data-restricted={cell?.restricted ? "true" : undefined}
      style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: PROJECT_STRIP_HEIGHT, boxSizing: "border-box", padding: "0 14px", minWidth: 0, borderLeft: `1px solid ${T.hairline ?? T.border}` }}>
      <div style={{ ...cellLabel, ...oneLine, lineHeight: "14px" }}>{label}</div>
      <div data-testid={`${testId}-value`} title={cell?.restricted ? undefined : cell?.value} style={{ ...oneLine, marginTop: 3, fontSize: T.fsLg, lineHeight: "22px", fontWeight: T.fwBold, fontVariantNumeric: "tabular-nums",
        color: cell?.restricted || cell?.gap ? T.mid : cell?.color || T.dark, fontStyle: cell?.restricted ? "italic" : "normal" }}>
        {loading ? <span aria-hidden="true" style={{ display: "inline-block", width: 44, height: 14, borderRadius: 4, background: T.lightGray }} /> : cell?.value ?? "–"}
      </div>
      <div data-testid={`${testId}-sub`} title={cell?.sub || undefined} style={{ ...oneLine, marginTop: 1, fontSize: T.fsXxs, lineHeight: "14px", color: T.mid }}>{loading ? "\u00a0" : cell?.sub || "\u00a0"}</div>
    </div>
  );
}

function PostedChip({ participation, href }) {
  const chip = participationChip(participation);
  const tone = chip.kind === "complete" ? { color: T.ragGreenText, background: T.ragGreenBg, borderColor: T.ragGreenBorder }
    : chip.kind === "incomplete" ? { color: T.ragAmberText, background: T.ragAmberBg, borderColor: T.ragAmber }
    : { color: T.mid, background: T.lightGray, borderColor: T.border };
  const style = { ...tone, display: "inline-flex", alignItems: "center", marginLeft: 8, padding: "0 7px", height: 18, borderRadius: 9, borderWidth: 1, borderStyle: "solid", fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0, textTransform: "none", textDecoration: "none", verticalAlign: "middle" };
  if (href) {
    const label = chip.kind === "complete" || chip.kind === "incomplete" ? chip.label : `${chip.label}. Open Updates`;
    return <InternalLink href={href} data-testid="project-record-posted-chip" data-state={chip.kind} aria-label={label} title={label} style={{ ...style, fontStyle: chip.kind === "unavailable" ? "italic" : "normal" }}>{chip.text}</InternalLink>;
  }
  return <span data-testid="project-record-posted-chip" data-state={chip.kind} aria-label={chip.label} title={chip.label} style={style}>{chip.text}</span>;
}

export function ProjectRecordDrawer({
  projectId, projectName = "", open, onClose, currentRole = "viewer", readOnly = false, mediaReadOnly = readOnly, frozen = false,
  origin = "portfolio", participation = null, onRecordSaved, onArchived, guardRef, orderedProjectIds = [], onOpenProject, navigationDisabled = false,
  recordTab = null, onRecordTabChange = null, copyPath = null, recordReturn = null,
}) {
  // Standalone readers are also used in cleanup-free component tests. The
  // routed application remains the sole URL owner; no manual location store.
  const location = useContext(UNSAFE_LocationContext)?.location;
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator;
  const bodyRef = useRef(null);
  const headerFocus = useRef(null);
  const returnFocus = useRef(null);
  const editor = useRef(IDLE);
  const [editorState, setEditorState] = useState(IDLE);
  const [ask, setAsk] = useState(null); // null | { proceed?: fn, label }
  const pending = useRef(null);
  const navigation = useRef(null);
  navigation.current = { active: open && Boolean(projectId), projectId, ids: orderedProjectIds, onOpenProject, disabled: navigationDisabled };
  const askOnce = (intent) => {
    if (pending.current) return;
    pending.current = intent;
    setAsk(intent);
  };
  const clearAsk = () => { pending.current = null; setAsk(null); };
  const requestLeave = useCallback(proceed => {
    const next = () => {
      if (editor.current.editing || editor.current.busy) askOnce({ proceed, label: "Discard and leave" });
      else proceed();
    };
    if (editor.current.media?.requestLeave) editor.current.media.requestLeave(next);
    else next();
  }, []);
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 640 : Math.min(680, window.innerWidth)));
  const active = open && Boolean(projectId);
  // Each open resets the record tabs to Overview (project changes remount).
  const [openToken, setOpenToken] = useState(0);
  useEffect(() => { if (active) setOpenToken((n) => n + 1); }, [active]);
  useEffect(() => active ? guardRouterNavigator(navigator) : undefined, [active, navigator]);

  const onEditorStateChange = useCallback((st) => {
    editor.current = { editing: Boolean(st?.editing), busy: Boolean(st?.busy), media: st?.media };
    setEditorState(editor.current);
  }, []);
  useEffect(() => { editor.current = IDLE; setEditorState(IDLE); pending.current = null; setAsk(null); }, [projectId, open]);

  const status = useCallback(() => (active ? {
    dirty: editor.current.editing || editor.current.busy || Boolean(editor.current.media?.editing || editor.current.media?.busy),
    busy: editor.current.busy || Boolean(editor.current.media?.busy),
  } : { dirty: false, busy: false }), [active]);
  useEffect(() => {
    if (!guardRef) return undefined;
    guardRef.current = status;
    guardRef.current.requestLeave = proceed => requestLeave(proceed);
    guardRef.current.beforeUnload = () => Boolean(editor.current.editing || editor.current.busy || editor.current.media?.editing || editor.current.media?.uploading);
    return () => { guardRef.current = null; };
  }, [guardRef, status, requestLeave]);

  // In-app navigation blocker (anchor clicks + back/forward) while dirty.
  useEffect(() => {
    syncNavigationIndex();
    const media = editor.current.media;
    if (media?.transient && !media.editing && !media.busy) media.requestLeave?.(() => {});
  }, [location?.key]);
  useEffect(() => {
    if (!active) return undefined;
    return registerNavigationGuard({
      isBlocking: () => status().dirty,
      onBlocked: (proceed) => requestLeave(proceed),
    });
  }, [active, status, requestLeave]);

  const panel = () => bodyRef.current?.closest(PANEL_SEL) || null;

  // The SlideSheet panel is the dialog so its Close button sits inside it.
  useEffect(() => {
    if (!active) return undefined;
    returnFocus.current = document.activeElement;
    const t = setTimeout(() => {
      const el = panel();
      if (el) { el.setAttribute("role", "dialog"); el.setAttribute("aria-modal", "true"); el.setAttribute("aria-labelledby", "record-drawer-title"); }
      bodyRef.current?.querySelector("[data-testid='record-drawer-title']")?.focus();
    }, 0);
    const onResize = () => setWidth(Math.min(680, window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    const onBefore = (e) => { if (editor.current.editing || editor.current.busy || editor.current.media?.editing || editor.current.media?.uploading) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("beforeunload", onBefore);
      const el = returnFocus.current;
      setTimeout(() => { if (el && el.isConnected && typeof el.focus === "function") el.focus(); }, 0);
    };
  }, [active]);

  // Resolve against the latest caller-owned sequence, including after a
  // confirmation has waited through a filter, refresh, or ordering change.
  const switchRecord = (target, from) => {
    const nav = navigation.current;
    if (!nav.active || nav.disabled || editor.current.busy || nav.projectId !== from ||
        !nav.ids.includes(from) || !nav.ids.includes(target) || target === from || !nav.onOpenProject) return;
    clearAsk();
    editor.current = IDLE;
    nav.onOpenProject(target);
    bodyRef.current?.querySelector("[data-testid='record-drawer-title']")?.focus();
  };
  const requestRecord = (target) => {
    if (pending.current || stackedDialogOpen(panel())) return;
    const nav = navigation.current;
    if (!nav.ids.includes(nav.projectId) || !nav.ids.includes(target) || !nav.onOpenProject) return;
    const next = () => {
      if (editor.current.editing || editor.current.busy) askOnce({ target, from: nav.projectId, label: "Discard and switch" });
      else switchRecord(target, nav.projectId);
    };
    if (editor.current.media?.requestLeave) editor.current.media.requestLeave(next);
    else next();
  };

  const forceClose = () => {
    if (editor.current.busy) return; // never abandon an in-flight mutation
    clearAsk(); editor.current = IDLE; onClose();
  };
  const requestClose = () => {
    if (stackedDialogOpen(panel())) return; // stacked dialog owns Escape/scrim
    requestLeave(forceClose);
  };
  const confirmDiscard = () => {
    if (editor.current.busy) return;
    const intent = pending.current;
    if (intent?.target) {
      switchRecord(intent.target, intent.from);
      clearAsk();
    } else if (intent?.proceed) { clearAsk(); editor.current = IDLE; intent.proceed(); } else forceClose();
  };

  // Focus containment, including from the programmatically focused title.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const el = panel();
      if (!el || stackedDialogOpen(el)) return;
      const items = [...el.querySelectorAll(FOCUSABLE)].filter((n) => !n.closest("[hidden]"));
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0]; const last = items[items.length - 1];
      const cur = document.activeElement;
      const inside = items.includes(cur);
      if (!inside) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && cur === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && cur === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [active]);

  const busy = editorState.busy;
  const sensitivity = useSensitivityPolicy();
  const canSeeBudget = sensitivity.canSee("projects.budget");
  // One loaded record + milestone projection from ProjectFullPage (no second
  // fetch). Values from any other project are ignored.
  const [projection, setProjection] = useState(null);
  const onRecordProjection = useCallback((p) => setProjection(p), []);
  const shown = projection?.projectId === projectId ? projection : null;
  const rec = shown?.project || null;
  const meta = headerMeta(rec);
  const search = location?.search ?? (typeof window === "undefined" ? "" : window.location.search);
  const projectCover = useProjectCover(projectId, active);
  const officeCover = useOfficeCover(rec?.locationId, active && !frozen && Boolean(rec?.locationId), `${projectId}:${currentRole}`, rec?.location?.imageUrl);
  const cover = projectCover?.intentionalEmpty ? null : projectCover || officeCover;
  // Introducing/removing the decorative band reparents its children. Preserve
  // keyboard focus on the title/pager, but never steal focus from an editor.
  useLayoutEffect(() => {
    if (active && headerFocus.current && document.activeElement === document.body) {
      bodyRef.current?.querySelector(`[data-testid="${headerFocus.current}"]`)?.focus();
    }
  }, [Boolean(cover), active]); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <SlideSheet open={active} onClose={requestClose} width={width} layer={LAYERS.stickyChrome + 1} panelTestId="project-record-drawer" scrimTestId="project-record-drawer-scrim" closeTestId="project-record-drawer-close" hideCloseButton>
      <style>{`
        [data-testid="project-record-drawer"] .wk-av { position: static; display: inline-flex; border-radius: 50%; }
        [data-testid="project-record-drawer"] .wk-avtip { position: absolute; bottom: 100%; left: 0; right: 0; box-sizing: border-box; background: ${T.dark}; color: ${T.white}; font-size: ${T.fsXs}px; padding: 5px 9px; border-radius: 7px; white-space: normal; overflow-wrap: anywhere; opacity: 0; pointer-events: none; z-index: 10; }
        [data-testid="project-record-drawer"] .wk-av:hover .wk-avtip,
        [data-testid="project-record-drawer"] .wk-av:focus-visible .wk-avtip { opacity: 1; }
        [data-testid="project-record-strip"] > :first-child { border-left: 0 !important; }
        [data-testid="project-record-cover-host"] { margin: -18px -22px 0; }
        @media (max-width: 480px) {
          [data-testid="project-record-cover-host"] { margin: -18px -14px 0; }
          [data-testid="record-drawer-body"] { padding-left: 14px !important; padding-right: 14px !important; }
          [data-testid="project-schedule"] > div:first-child { flex-wrap: wrap; }
        }
      `}</style>
      <div ref={bodyRef} data-testid="record-drawer-body" onFocusCapture={event => {
        headerFocus.current = event.target.closest("header") ? event.target.getAttribute("data-testid") : null;
      }} style={{ padding: "18px 22px 40px", boxSizing: "border-box", minHeight: "100%", background: T.tint || T.lightGray }}>
        <div data-testid="project-record-cover-host">
        <CoverBand cover={cover} minHeight={PROJECT_HEADER_HEIGHT} marginBottom={0}>
        <RecordDrawerHeader testId="project-record-header" height={PROJECT_HEADER_HEIGHT} stripHeight={PROJECT_STRIP_HEIGHT} padding="14px 22px 0"
          returnLink={recordReturn && <RecordReturnLink inline entry={recordReturn} navigate={(to, state) => navigator?.push(to, state)} />}
          eyebrow={<>Project{currentRole === "admin" && <PostedChip participation={participation} href={projectId ? buildProjectUpdatesUrl(projectId, search) : null} />}</>}
          eyebrowColor={cover ? T.dark : T.mid}
          titleId="record-drawer-title" titleTestId="record-drawer-title" title={projectName || rec?.name || "Project"} titleText={projectName || rec?.name || undefined}
          titleColor={T.projectsHubNavy}
          meta={rec ? <>
            {meta.code && <span data-testid="project-record-code" style={{ fontWeight: T.fwSemibold, color: T.dark }}>{meta.code}</span>}
            {meta.code && meta.type && <span aria-hidden="true"> · </span>}
            {meta.type && <span data-testid="project-record-type">{meta.type}</span>}
            {(meta.code || meta.type) && meta.office && <span aria-hidden="true"> · </span>}
            {meta.office && (rec.locationId
              ? <InternalLink href={buildLocationUrl(rec.locationId)} returnRecord={{ kind: "project", label: rec.name || projectName }} data-testid="project-record-office-link" style={{ color: T.blue, textDecoration: "none", fontWeight: T.fwSemibold }}>{meta.office}</InternalLink>
               : <span data-testid="project-record-office">{meta.office}</span>)}
            {(meta.code || meta.type || meta.office) && <span aria-hidden="true"> · </span>}
            <ProjectStagePill stage={rec.stage} />
          </> : "\u00a0"}
          metaText={meta.text || undefined} metaTestId="project-record-meta" metaColor={cover ? T.dark : T.mid}
          share={copyPath ? { path: copyPath, testId: "project-record-copy-link" } : null}
          fullPage={projectId && navigator ? { testId: "project-record-full-page", onClick: () => {
            if (pending.current || stackedDialogOpen(panel())) return;
            navigator.push(buildProjectFullUrl(projectId, search), {
              projectFullReturn: { to: `${location?.pathname || "/projects"}${search}`, state: location?.state ?? null },
            });
          } } : null}
          pager={{ ids: orderedProjectIds, currentId: projectId, onNavigate: requestRecord, keyboardActive: active,
             disabled: navigationDisabled || !onOpenProject || Boolean(ask), busy: false, showUnavailable: true,
            previousLabel: "Previous project", nextLabel: "Next project", positionTestId: "project-record-position" }}
          onClose={requestClose} closeTestId="project-record-drawer-close" closeLabel="Close project"
          strip={(
            <div data-testid="project-record-strip" aria-busy={!rec || undefined}
              style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", height: PROJECT_STRIP_HEIGHT, boxSizing: "border-box", overflow: "hidden",
                background: cover ? T.coverPanelBg ?? T.card : T.card, backdropFilter: cover ? T.coverPanelBlur : undefined, WebkitBackdropFilter: cover ? T.coverPanelBlur : undefined,
                border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.cardShadow }}>
              <StripCell testId="project-strip-budget" label={rec?.stage === "completed" ? "Final budget" : "Budget"} loading={!rec} cell={rec ? budgetCell(rec, canSeeBudget) : null} />
              <StripCell testId="project-strip-phase" label="Phase" loading={!rec} cell={rec ? phaseCell(rec) : null} />
              <StripCell testId="project-strip-fdob" label="FDOB" loading={!rec} cell={rec ? fdobCell(rec) : null} />
              <StripCell testId="project-strip-status" label={isTerminalProject(rec) ? "Stage" : "Status"} loading={!rec} cell={rec ? statusCell(rec, shown.milestones) : null} />
            </div>
          )} />
        </CoverBand>
        </div>

        {ask && (
          <div role="alert" data-testid="record-drawer-unsaved" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", borderRadius: 8, background: T.utilAmber, fontSize: T.fsSm, marginBottom: 10 }}>
            <span data-testid={busy ? "record-drawer-wait" : "record-drawer-unsaved-text"} style={{ flex: 1, minWidth: 160 }}>
              {busy ? "A record change is still saving. Wait for it to finish before leaving." : "The record has an open editor. Leaving now discards unsaved changes."}
            </span>
            <button type="button" data-testid="record-drawer-keep" onClick={clearAsk} style={btn}>Keep editing</button>
            <button type="button" data-testid="record-drawer-discard" disabled={busy} onClick={confirmDiscard} style={{ ...btn, opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}>{ask.label}</button>
          </div>
        )}

        {projectId && (
          <ProjectFullPage key={projectId} projectId={projectId} currentRole={currentRole} readOnly={readOnly || frozen} mediaReadOnly={mediaReadOnly || frozen} recordOnly
            onArchived={(id) => { clearAsk(); editor.current = IDLE; onArchived?.(id); onClose(); }}
            participation={participation} frozen={frozen} onRecordProjection={onRecordProjection}
            onRecordSaved={onRecordSaved} onEditorStateChange={onEditorStateChange} recordTabResetToken={openToken} recordTab={recordTab} onRecordTabChange={onRecordTabChange} />
        )}
      </div>
    </SlideSheet>, document.body
  );
}

export default ProjectRecordDrawer;
